import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Download, Eye, Pencil, Trash2, ExternalLink, Copy, Loader2 } from 'lucide-react';

const STAGES = [
  { id: 'new', label: 'New' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'proposal_sent', label: 'Proposal Sent' },
  { id: 'meeting_booked', label: 'Meeting Booked' },
  { id: 'won', label: 'Won' },
  { id: 'lost', label: 'Lost' },
];
const STAGE_COLORS = {
  new: 'text-gray-400', contacted: 'text-sky-400', proposal_sent: 'text-violet-400',
  meeting_booked: 'text-amber-400', won: 'text-emerald-400', lost: 'text-red-400',
};

export const ProposalsPanel = ({ theme, api, workspaceId, onEdit }) => {
  const { cardBg, cardBorder, textPrimary, textSecondary, inputBg } = theme;
  const [props, setProps] = useState([]);
  const [status, setStatus] = useState('');
  const [stage, setStage] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/workspaces/${workspaceId}/leads-proposals?status=${status}&q=${encodeURIComponent(q)}`);
      setProps(r.data.proposals || []);
    } catch (e) { /* ignore */ }
  }, [api, workspaceId, status, q]);

  useEffect(() => { load(); }, [load]);

  // Poll while any proposal is still generating
  const hasGenerating = props.some(p => p.status === 'generating');
  useEffect(() => {
    if (!hasGenerating) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [hasGenerating, load]);

  const copyLink = (p) => {
    navigator.clipboard.writeText(`${window.location.origin}/p/${p.token}`);
    toast.success('Proposal link copied');
  };

  const downloadPdf = async (p) => {
    try {
      const r = await api.get(`/api/workspaces/${workspaceId}/leads-proposals/${p.proposal_id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `${p.business_name}_proposal.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('PDF download failed'); }
  };

  const setLeadStage = async (p, s) => {
    try {
      await api.put(`/api/workspaces/${workspaceId}/leads/${p.lead_id}`, { status: s });
      toast.success(`Moved to ${s.replace(/_/g, ' ')}`);
      load();
    } catch { toast.error('Failed to update stage'); }
  };

  const remove = async (p) => {
    if (!window.confirm('Delete this proposal?')) return;
    await api.delete(`/api/workspaces/${workspaceId}/leads-proposals/${p.proposal_id}`);
    load();
  };

  const exportCsv = async () => {
    try {
      const r = await api.get(`/api/workspaces/${workspaceId}/leads-proposals/export/csv?status=${status}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = 'proposals.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  return (
    <div className={`rounded-2xl border ${cardBorder} ${cardBg}`} data-testid="proposals-panel">
      <div className={`flex flex-wrap items-center gap-2 px-4 py-3 border-b ${cardBorder}`}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search business…"
          className={`px-2.5 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-xs focus:outline-none w-52`}
          data-testid="proposals-search" />
        <select value={status} onChange={e => setStatus(e.target.value)}
          className={`px-2.5 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-xs`}
          data-testid="proposals-status-filter">
          <option value="">All</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
        </select>
        <div className="flex-1" />
        <button onClick={exportCsv}
          className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border ${cardBorder} ${textPrimary} ${inputBg}`}
          data-testid="proposals-export-btn">
          <Download size={12} /> Export CSV
        </button>
      </div>
      {/* Pipeline summary — stages live on the lead, synced everywhere */}
      <div className={`flex items-center gap-1.5 px-4 py-2.5 border-b ${cardBorder} overflow-x-auto`} data-testid="proposals-pipeline-strip">
        <button onClick={() => setStage('')}
          className={`text-[11px] px-3 py-1.5 rounded-full border font-semibold whitespace-nowrap transition-colors ${
            stage === '' ? 'bg-teal-400/15 text-teal-300 border-teal-400/40' : `${cardBorder} ${textSecondary}`}`}
          data-testid="pipeline-stage-all">
          All · {props.length}
        </button>
        {STAGES.map(s => {
          const n = props.filter(p => (p.lead_stage || 'new') === s.id).length;
          return (
            <button key={s.id} onClick={() => setStage(stage === s.id ? '' : s.id)}
              className={`text-[11px] px-3 py-1.5 rounded-full border font-semibold whitespace-nowrap transition-colors ${
                stage === s.id ? 'bg-teal-400/15 text-teal-300 border-teal-400/40' : `${cardBorder} ${STAGE_COLORS[s.id]}`}`}
              data-testid={`pipeline-stage-${s.id}`}>
              {s.label} · {n}
            </button>
          );
        })}
      </div>
      <div className="divide-y divide-white/5">
        {props.length === 0 && (
          <p className={`px-4 py-10 text-center text-sm ${textSecondary}`} data-testid="proposals-empty">
            No proposals yet — analyze a lead and hit "Generate proposal".
          </p>
        )}
        {props.filter(p => !stage || (p.lead_stage || 'new') === stage).map(p => (
          <div key={p.proposal_id} className="px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`proposal-row-${p.proposal_id}`}>
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium ${textPrimary} truncate`}>{p.business_name}</p>
              {p.status === 'generating' ? (
                <p className="text-[11px] text-violet-400 flex items-center gap-1.5 mt-0.5" data-testid={`proposal-generating-${p.proposal_id}`}>
                  <Loader2 size={11} className="animate-spin" /> Generating proposal — writing the leak breakdown, solutions and deploy plan…
                </p>
              ) : (
                <p className={`text-[11px] ${textSecondary} flex items-center gap-2 mt-0.5 flex-wrap`}>
                  <span>{p.industry} · {p.city}</span>
                  <span className="text-red-400 font-medium">${Number(p.total_monthly_leak_usd || 0).toLocaleString()}/mo leak</span>
                  <span className={`px-1.5 py-0.5 rounded-full border text-[10px] ${
                    p.status === 'sent' ? 'bg-blue-400/10 text-blue-400 border-blue-400/20'
                      : p.status === 'error' ? 'bg-red-400/10 text-red-400 border-red-400/20'
                        : 'bg-gray-400/10 text-gray-400 border-gray-400/20'}`}>
                    {p.status === 'error' ? 'failed' : p.status}
                  </span>
                  {p.view_count > 0 && (
                    <span className="flex items-center gap-0.5 text-teal-400"><Eye size={10} /> viewed {p.view_count}×</span>
                  )}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {p.status !== 'generating' && (
                <select value={p.lead_stage || 'new'} onChange={e => setLeadStage(p, e.target.value)}
                  onClick={e => e.stopPropagation()}
                  className={`text-[11px] px-2 py-1.5 rounded-lg border ${cardBorder} ${inputBg} font-semibold ${STAGE_COLORS[p.lead_stage || 'new']}`}
                  data-testid={`proposal-stage-${p.proposal_id}`}>
                  {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              )}
              {p.status === 'generating' && (
                <Loader2 size={16} className="animate-spin text-violet-400" />
              )}
              {(p.status === 'draft' || p.status === 'sent') && (
                <>
                  <button onClick={() => onEdit(p)} title="Edit"
                    className={`p-2 rounded-lg border ${cardBorder} ${textSecondary} hover:text-teal-400`}
                    data-testid={`proposal-edit-${p.proposal_id}`}><Pencil size={13} /></button>
                  <button onClick={() => copyLink(p)} title="Copy public link"
                    className={`p-2 rounded-lg border ${cardBorder} ${textSecondary} hover:text-teal-400`}
                    data-testid={`proposal-copy-${p.proposal_id}`}><Copy size={13} /></button>
                  <a href={`/p/${p.token}`} target="_blank" rel="noreferrer" title="Open public page"
                    className={`p-2 rounded-lg border ${cardBorder} ${textSecondary} hover:text-teal-400`}
                    data-testid={`proposal-open-${p.proposal_id}`}><ExternalLink size={13} /></a>
                  <button onClick={() => downloadPdf(p)} title="Download PDF"
                    className={`p-2 rounded-lg border ${cardBorder} ${textSecondary} hover:text-teal-400`}
                    data-testid={`proposal-pdf-${p.proposal_id}`}><Download size={13} /></button>
                </>
              )}
              {p.status !== 'generating' && (
                <button onClick={() => remove(p)} title="Delete"
                  className={`p-2 rounded-lg border ${cardBorder} ${textSecondary} hover:text-red-400`}
                  data-testid={`proposal-delete-${p.proposal_id}`}><Trash2 size={13} /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProposalsPanel;
